import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Alert,
} from "@mui/material";
import { Edit as EditIcon, Close as CloseIcon } from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers";
import {
  dxtToLocalServerTime,
  getMomentTz,
  TimeService,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { Moment } from "moment";
import type { TaskHistoryEntry } from "../../models";
import {
  getTaskHistoryWithLocationContext,
  getOperationBounds,
} from "../../repositories/SharedEqpRepository";

const moment = getMomentTz();

interface Props {
  open: boolean;
  partId: number | null;
  partName: string | null;
  onClose: () => void;
  onSave: (taskId: number, newTimestamp: Date) => Promise<boolean>;
  /** Whether to validate against CASTER_EQP_INITIAL_DATETIME setting. Only for Caster mold module. */
  validateAgainstInitialDatetime?: boolean;
}

export const CorrectDatesModal = ({
  open,
  partId,
  partName,
  onClose,
  onSave,
  validateAgainstInitialDatetime = false,
}: Props) => {
  const [tasks, setTasks] = useState<TaskHistoryEntry[]>([]);
  const [nextOccupantInstallTimestamp, setNextOccupantInstallTimestamp] =
    useState<Date | null>(null);
  const [centralBounds, setCentralBounds] = useState<{
    lowerBound: Date | null;
    upperBound: Date | null;
    lowerBoundSource: string | null;
    upperBoundSource: string | null;
  } | null>(null);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<Moment | null>(null);
  const { state: utlState } = useUTLSettingsContext();

  // Get CASTER_EQP_INITIAL_DATETIME from settings context (default: 2026-01-01T00:00:00.000Z)
  const casterInitialDatetime = useMemo(() => {
    if (!validateAgainstInitialDatetime) return null;
    const settingValue = get(
      utlState.settings,
      "CASTER_EQP_INITIAL_DATETIME",
      "2026-01-01T00:00:00.000Z",
    );
    return moment.utc(settingValue as string).toDate();
  }, [utlState.settings, validateAgainstInitialDatetime]);

  const loadTasks = useCallback(async () => {
    if (!partId) return;
    setIsLoadingTasks(true);
    try {
      const result = await getTaskHistoryWithLocationContext(partId);
      setTasks(result.tasks);
      setNextOccupantInstallTimestamp(result.nextOccupantInstallTimestamp);

      // Fetch centralized bounds based on whether the part is installed or uninstalled
      const isUninstalled =
        result.tasks.length > 0 && result.tasks[0].IsCurrentlyUninstalled;
      const action = isUninstalled ? "correct_uninstall" : "correct_install";
      const bounds = await getOperationBounds(partId, action);
      setCentralBounds(bounds);
    } finally {
      setIsLoadingTasks(false);
    }
  }, [partId]);

  useEffect(() => {
    if (open && partId) {
      setEditingTaskId(null);
      setEditValue(null);
      loadTasks();
    } else {
      setTasks([]);
      setEditingTaskId(null);
      setEditValue(null);
    }
  }, [open, partId, loadTasks]);

  const handleEdit = (task: TaskHistoryEntry) => {
    setEditingTaskId(task.TaskId);
    setEditValue(moment(task.Timestamp));
  };

  const handleCancel = () => {
    setEditingTaskId(null);
    setEditValue(null);
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (taskId: number) => {
    if (!editValue) return;
    setIsSaving(true);
    const success = await onSave(taskId, editValue.toDate());
    setIsSaving(false);
    if (success) {
      setEditingTaskId(null);
      setEditValue(null);
      loadTasks();
    }
  };

  const getValidationError = (task: TaskHistoryEntry): string | null => {
    if (!editValue || !editValue.isValid()) return "Invalid date";

    const originalMoment = moment(task.Timestamp);
    if (editValue.isSame(originalMoment)) return "Date has not changed";
    if (editValue.isAfter(moment())) return "Date cannot be in the future";

    // Use centralized bounds from RBD.EQPGetOperationBounds when available
    if (centralBounds && task.Action !== "Repaired") {
      const lower = centralBounds.lowerBound
        ? moment.utc(centralBounds.lowerBound)
        : null;
      const upper = centralBounds.upperBound
        ? moment.utc(centralBounds.upperBound)
        : null;

      if (lower && editValue.isSameOrBefore(lower))
        return `Date must be after ${centralBounds.lowerBoundSource ?? "constraint"} (${dxtToLocalServerTime(lower.toDate(), "MM/dd/yyyy HH:mm")})`;
      if (upper && editValue.isSameOrAfter(upper))
        return `Date must be before ${centralBounds.upperBoundSource ?? "constraint"} (${dxtToLocalServerTime(upper.toDate(), "MM/dd/yyyy HH:mm")})`;

      return null;
    }

    // Fallback: repair tasks or when centralBounds not loaded
    if (task.Action === "Repaired" && casterInitialDatetime) {
      if (editValue.isSameOrBefore(moment.utc(casterInitialDatetime)))
        return `Date must be after initial date limit (${dxtToLocalServerTime(casterInitialDatetime, "MM/dd/yyyy HH:mm")})`;
    }

    return null;
  };

  const editingTask = tasks.find((t) => t.TaskId === editingTaskId);
  const validationError = editingTask ? getValidationError(editingTask) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Correct Dates {partName ? `— ${partName}` : ""}</DialogTitle>
      <DialogContent>
        {isLoadingTasks ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress />
          </Box>
        ) : tasks.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No tasks found for this part.
          </Typography>
        ) : (
          <>
            {tasks[0]?.IsCurrentlyUninstalled ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This part is not currently installed. Correcting the date will
                adjust Total and Campaign for the previous installation period.
              </Alert>
            ) : null}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell width={50} />
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task, index) => (
                  <React.Fragment key={task.TaskId}>
                    <TableRow
                      hover={index === 0 && Boolean(task.IsEditable)}
                      selected={editingTaskId === task.TaskId}
                      sx={{
                        "& > *": {
                          borderBottom:
                            editingTaskId === task.TaskId
                              ? "none !important"
                              : undefined,
                        },
                        opacity: task.IsEditable ? 1 : 0.5,
                      }}
                    >
                      <TableCell>
                        {dxtToLocalServerTime(
                          task.Timestamp,
                          "MM/dd/yyyy HH:mm",
                        )}
                      </TableCell>
                      <TableCell>{task.Action}</TableCell>
                      <TableCell>{task.LocationName ?? "-"}</TableCell>
                      <TableCell>
                        {!task.IsEditable ||
                        index !== 0 ? null : editingTaskId === task.TaskId ? (
                          <IconButton
                            size="small"
                            onClick={handleCancel}
                            disabled={isSaving}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(task)}
                            disabled={isSaving || editingTaskId !== null}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={4} sx={{ p: 0 }}>
                        <Collapse
                          in={editingTaskId === task.TaskId}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.5,
                              p: 2,
                              bgcolor: "action.hover",
                            }}
                          >
                            {task.Action === "Repaired" ? (
                              <Alert severity="info" variant="outlined">
                                {casterInitialDatetime ? (
                                  <>
                                    Must be after{" "}
                                    <strong>
                                      {dxtToLocalServerTime(
                                        casterInitialDatetime,
                                        "MM/dd/yyyy HH:mm",
                                      )}
                                    </strong>
                                    {" and before "}
                                    <strong>now</strong>
                                  </>
                                ) : (
                                  "Repair can be placed at any date (must not be in the future)."
                                )}
                              </Alert>
                            ) : (
                              <Alert severity="info" variant="outlined">
                                {centralBounds ? (
                                  <>
                                    {centralBounds.lowerBound ? (
                                      <>
                                        Must be after{" "}
                                        <strong>
                                          {dxtToLocalServerTime(
                                            centralBounds.lowerBound,
                                            "MM/dd/yyyy HH:mm",
                                          )}
                                        </strong>
                                        {centralBounds.lowerBoundSource
                                          ? ` (${centralBounds.lowerBoundSource})`
                                          : ""}
                                        {" and before "}
                                      </>
                                    ) : (
                                      <>Must be before </>
                                    )}
                                    <strong>
                                      {centralBounds.upperBoundSource === "now"
                                        ? "now"
                                        : centralBounds.upperBound
                                          ? dxtToLocalServerTime(
                                              centralBounds.upperBound,
                                              "MM/dd/yyyy HH:mm",
                                            )
                                          : "now"}
                                    </strong>
                                    {centralBounds.upperBoundSource &&
                                    centralBounds.upperBoundSource !== "now"
                                      ? ` (${centralBounds.upperBoundSource})`
                                      : ""}
                                  </>
                                ) : (
                                  <>Loading constraints...</>
                                )}
                              </Alert>
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 2,
                              }}
                            >
                              <DateTimePicker
                                value={editValue}
                                onChange={(val) => setEditValue(val)}
                                timezone={TimeService.getInstance().getServerTimeZone()}
                                format="MM/DD/YYYY HH:mm"
                                ampm={false}
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    error: !!validationError,
                                    helperText: validationError ?? " ",
                                    sx: { minWidth: 240 },
                                  },
                                }}
                              />
                              <Button
                                variant="contained"
                                size="small"
                                sx={{ mt: "8px" }}
                                onClick={() => handleSave(task.TaskId)}
                                disabled={isSaving || !!validationError}
                                startIcon={
                                  isSaving ? (
                                    <CircularProgress size="1rem" />
                                  ) : undefined
                                }
                              >
                                Save
                              </Button>
                              <Button
                                size="small"
                                sx={{ mt: "8px" }}
                                onClick={handleCancel}
                                disabled={isSaving}
                              >
                                Cancel
                              </Button>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </>
        )}
        {tasks.length > 0 &&
        tasks[0].TotalCount > tasks.filter((t) => t.IsEditable).length ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            Showing {tasks.filter((t) => t.IsEditable).length} of{" "}
            {tasks[0].TotalCount} tasks.
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
